/**
 * Supabase implementation of ICheckDraftRepository.
 *
 * Key behaviours:
 * - transitionState: updates current_state, records event in check_draft_events,
 *   and (if transitioning to cashed/debited with a bank_account_id) inserts a
 *   bank_transactions row. Done sequentially (best-effort — Supabase JS SDK
 *   does not expose PG transactions from the browser; a Postgres function would
 *   be needed for true atomicity but that would violate the "no business logic
 *   in DB" rule in CLAUDE.md).
 */
import type { TypedSupabaseClient } from '../client';
import type {
  ICheckDraftRepository,
  CheckDraft,
  CheckDraftEvent,
  CheckDraftFilters,
  CheckDraftState,
  CreateCheckDraftInput,
  TransitionStateOptions,
  UpdateCheckDraftInput,
} from '../../../interfaces/src';

// States that trigger a bank_transactions entry when reached
const CASH_STATES: ReadonlySet<CheckDraftState> = new Set(['cashed', 'debited']);
const PROTESTABLE_COLUMN = 'protestable';

export class SupabaseCheckDraftRepository implements ICheckDraftRepository {
  // Cast to any: new tables (check_draft_events, tn_banks) are not yet in
  // the generated database.types.ts. Regen with `supabase gen types typescript`
  // after running migration 20260418120200.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly sb: any;
  private supportsProtestableColumn = true;

  constructor(client: TypedSupabaseClient) {
    this.sb = client;
  }

  // ---- list ----

  async list(filters?: CheckDraftFilters): Promise<CheckDraft[]> {
    let q = this.sb.from('checks_drafts').select('*');

    if (filters?.type) q = q.eq('type', filters.type);
    if (filters?.direction) q = q.eq('direction', filters.direction);
    if (filters?.currentState) q = q.eq('current_state', filters.currentState);
    if (filters?.thirdPartyType) q = q.eq('third_party_type', filters.thirdPartyType);
    if (filters?.thirdPartyId) q = q.eq('third_party_id', filters.thirdPartyId);
    if (filters?.bankAccountId) q = q.eq('bank_account_id', filters.bankAccountId);
    if (filters?.dueDateFrom ?? filters?.dueAfter) {
      q = q.gte('due_date', filters?.dueDateFrom ?? filters?.dueAfter);
    }
    if (filters?.dueDateTo ?? filters?.dueBefore) {
      q = q.lte('due_date', filters?.dueDateTo ?? filters?.dueBefore);
    }

    const { data, error } = await q.order('due_date', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(mapCheckDraft);
  }

  // ---- getById ----

  async getById(id: string): Promise<CheckDraft | null> {
    const { data, error } = await this.sb
      .from('checks_drafts')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapCheckDraft(data);
  }

  // ---- create ----

  async create(input: CreateCheckDraftInput): Promise<CheckDraft> {
    const payload: Record<string, unknown> = {
        type:             input.type,
        direction:        input.direction,
        number:           input.number,
        bank_name:        input.bankName ?? '',
        bank_account_id:  input.bankAccountId ?? null,
        payer_bank_id:    input.payerBankId ?? null,
        payer_rib:        input.payerRib ?? null,
        beneficiary_name: input.beneficiaryName ?? null,
        place_of_payment: input.placeOfPayment ?? null,
        place_of_issue:   input.placeOfIssue ?? null,
        amount:           input.amount,
        issue_date:       input.issueDate,
        due_date:         input.dueDate,
        acceptance_date:  input.acceptanceDate ?? null,
        third_party_type: input.thirdPartyType,
        third_party_id:   input.thirdPartyId,
        current_state:    input.currentState ?? (input.direction === 'received' ? 'in_wallet' : 'issued'),
        is_endorsed:      input.isEndorsed ?? false,
        endorsed_to:      input.endorsedTo ?? null,
        payment_id:       input.paymentId ?? null,
        notes:            input.notes ?? null,
    };
    if (this.supportsProtestableColumn) {
      payload[PROTESTABLE_COLUMN] = input.protestable ?? false;
    }

    let { data, error } = await this.sb
      .from('checks_drafts')
      .insert(payload)
      .select()
      .single();
    if (isMissingColumnError(error, PROTESTABLE_COLUMN)) {
      this.supportsProtestableColumn = false;
      delete payload[PROTESTABLE_COLUMN];
      ({ data, error } = await this.sb
        .from('checks_drafts')
        .insert(payload)
        .select()
        .single());
    }
    if (error) throw error;

    // Record creation event
    await this._insertEvent({
      checkDraftId: (data as Record<string, unknown>)['id'] as string,
      fromState: null,
      toState: ((data as Record<string, unknown>)['current_state'] as CheckDraftState),
      eventDate: ((data as Record<string, unknown>)['issue_date'] as string) ?? new Date().toISOString().slice(0, 10),
    });

    return mapCheckDraft(data);
  }

  // ---- update ----

  async update(id: string, input: UpdateCheckDraftInput): Promise<CheckDraft> {
    const mapped: Record<string, unknown> = {};
    if (input.number !== undefined)          mapped['number']           = input.number;
    if (input.bankName !== undefined)        mapped['bank_name']        = input.bankName;
    if (input.bankAccountId !== undefined)   mapped['bank_account_id']  = input.bankAccountId;
    if (input.payerBankId !== undefined)     mapped['payer_bank_id']    = input.payerBankId;
    if (input.payerRib !== undefined)        mapped['payer_rib']        = input.payerRib;
    if (input.beneficiaryName !== undefined) mapped['beneficiary_name'] = input.beneficiaryName;
    if (input.placeOfPayment !== undefined)  mapped['place_of_payment'] = input.placeOfPayment;
    if (input.placeOfIssue !== undefined)    mapped['place_of_issue']   = input.placeOfIssue;
    if (input.protestable !== undefined && this.supportsProtestableColumn) {
      mapped[PROTESTABLE_COLUMN] = input.protestable;
    }
    if (input.amount !== undefined)          mapped['amount']           = input.amount;
    if (input.issueDate !== undefined)       mapped['issue_date']       = input.issueDate;
    if (input.dueDate !== undefined)         mapped['due_date']         = input.dueDate;
    if (input.acceptanceDate !== undefined)  mapped['acceptance_date']  = input.acceptanceDate;
    if (input.thirdPartyType !== undefined)  mapped['third_party_type'] = input.thirdPartyType;
    if (input.thirdPartyId !== undefined)    mapped['third_party_id']   = input.thirdPartyId;
    if (input.currentState !== undefined)    mapped['current_state']    = input.currentState;
    if (input.isEndorsed !== undefined)      mapped['is_endorsed']      = input.isEndorsed;
    if (input.endorsedTo !== undefined)      mapped['endorsed_to']      = input.endorsedTo;
    if (input.paymentId !== undefined)       mapped['payment_id']       = input.paymentId;
    if (input.notes !== undefined)           mapped['notes']            = input.notes;

    if (Object.keys(mapped).length === 0) {
      const current = await this.getById(id);
      if (!current) throw new Error(`CheckDraft ${id} not found`);
      return current;
    }

    let { data, error } = await this.sb
      .from('checks_drafts')
      .update(mapped)
      .eq('id', id)
      .select()
      .single();
    if (isMissingColumnError(error, PROTESTABLE_COLUMN)) {
      this.supportsProtestableColumn = false;
      delete mapped[PROTESTABLE_COLUMN];
      if (Object.keys(mapped).length === 0) {
        const current = await this.getById(id);
        if (!current) throw new Error(`CheckDraft ${id} not found`);
        return current;
      }
      ({ data, error } = await this.sb
        .from('checks_drafts')
        .update(mapped)
        .eq('id', id)
        .select()
        .single());
    }
    if (error) throw error;

    const updated = mapCheckDraft(data);

    // Self-heal: if the draft is now in a settled state with a bank account,
    // make sure the matching bank_transactions row exists. Covers the case
    // where the user sets bank_account_id after the draft was already cashed,
    // or fixes legacy data that pre-dated the auto-creation logic.
    if (CASH_STATES.has(updated.currentState) && updated.bankAccountId) {
      await this._insertBankTransaction({
        bankAccountId:   updated.bankAccountId,
        direction:       updated.currentState === 'cashed' ? 'in' : 'out',
        transactionType: updated.type,
        amount:          updated.amount,
        transactionDate: updated.issueDate || new Date().toISOString().slice(0, 10),
        reference:       updated.number,
        checkDraftId:    updated.id,
        notes:           updated.notes,
      });
    }

    return updated;
  }

  // ---- delete ----

  async delete(id: string): Promise<void> {
    const { error } = await this.sb.from('checks_drafts').delete().eq('id', id);
    if (error) throw error;
  }

  // ---- updateState (legacy, kept for backward compat) ----

  /** @deprecated Use transitionState */
  async updateState(id: string, newState: string): Promise<CheckDraft> {
    return this.transitionState(id, newState as CheckDraftState);
  }

  // ---- transitionState ----

  async transitionState(
    id: string,
    toState: CheckDraftState,
    options?: TransitionStateOptions,
  ): Promise<CheckDraft> {
    // 1. Read current state before updating
    const current = await this.getById(id);
    if (!current) throw new Error(`CheckDraft ${id} not found`);

    const fromState = current.currentState;
    const eventDate = options?.eventDate ?? new Date().toISOString().slice(0, 10);

    // 2. Update current_state
    const { data, error } = await this.sb
      .from('checks_drafts')
      .update({ current_state: toState })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;

    // 3. Record event
    await this._insertEvent({
      checkDraftId: id,
      fromState,
      toState,
      eventDate,
      notes: options?.notes,
    });

    // 4. If transitioning to cashed/debited AND bank_account_id is set,
    //    insert a bank_transactions row.
    //
    //    bank_transactions is created by the Treasury migration (20260418120000).
    //    direction = 'in'  for cashed   (we receive money)
    //    direction = 'out' for debited  (we pay money out)
    if (CASH_STATES.has(toState) && current.bankAccountId) {
      await this._insertBankTransaction({
        bankAccountId:  current.bankAccountId,
        direction:      toState === 'cashed' ? 'in' : 'out',
        transactionType: current.type,          // 'check' | 'draft'
        amount:         current.amount,
        transactionDate: eventDate,
        reference:      current.number,
        checkDraftId:   id,
        notes:          options?.notes ?? null,
      });
    }

    // 5. If this check/traite carries a payment AND reaches a settled state,
    //    propagate payment_id to any withholding_certificates that were
    //    allocated to the payment at creation time. C'est ce qui fait passer
    //    la retenue de "En attente" à "Payé" à l'échéance de la traite.
    if (CASH_STATES.has(toState) && current.paymentId) {
      await this._markAllocatedCertificatesPaid(current.paymentId);
    }

    return mapCheckDraft(data);
  }

  private async _markAllocatedCertificatesPaid(paymentId: string): Promise<void> {
    const { data: allocs, error } = await this.sb
      .from('payment_allocations')
      .select('document_id')
      .eq('payment_id', paymentId)
      .eq('document_type', 'withholding');
    if (error) {
      console.warn('[checks-repository] failed to load payment_allocations:', error.message);
      return;
    }
    const certIds = (allocs ?? []).map((a: Record<string, unknown>) => a['document_id'] as string).filter(Boolean);
    if (certIds.length === 0) return;
    const { error: upErr } = await this.sb
      .from('withholding_certificates')
      .update({ payment_id: paymentId })
      .in('id', certIds)
      .is('payment_id', null); // ne jamais écraser un paiement déjà posé
    if (upErr) {
      console.warn('[checks-repository] failed to mark certificates paid:', upErr.message);
    }
  }

  // ---- listEvents ----

  async listEvents(checkDraftId: string): Promise<CheckDraftEvent[]> {
    const { data, error } = await this.sb
      .from('check_draft_events')
      .select('*')
      .eq('check_draft_id', checkDraftId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapEvent);
  }

  // ---- getDueSoon ----

  async getDueSoon(): Promise<CheckDraft[]> {
    const today = new Date();
    const todayISO  = today.toISOString().slice(0, 10);
    const in7DaysISO = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);

    // Terminal states — items in these states need no alert
    const terminalStates: CheckDraftState[] = ['cashed', 'bounced', 'debited', 'rejected'];

    const { data, error } = await this.sb
      .from('checks_drafts')
      .select('*')
      .gte('due_date', todayISO)
      .lte('due_date', in7DaysISO)
      .not('current_state', 'in', `(${terminalStates.join(',')})`)
      .order('due_date', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(mapCheckDraft);
  }

  // ---- backfill ----

  /**
   * Scan all settled (cashed/debited) drafts that have a bank_account_id but
   * no matching bank_transactions row, and insert the missing rows. Idempotent.
   *
   * Used by the "Recalculate balance" action in the treasury UI to repair
   * legacy data — e.g. drafts settled before the auto-creation code existed,
   * or drafts whose bank_account_id was assigned after they were already cashed.
   *
   * Drafts in cashed/debited state with bank_account_id = NULL cannot be
   * backfilled here (no destination account known) — the user must edit the
   * draft to assign one, which triggers `update()`'s self-heal.
   */
  async backfillBankTransactions(): Promise<{ created: number }> {
    // 1. All drafts in a settled state with a bank account.
    const { data: drafts, error: draftsErr } = await this.sb
      .from('checks_drafts')
      .select('id, type, current_state, bank_account_id, amount, number, issue_date, notes')
      .in('current_state', ['cashed', 'debited'])
      .not('bank_account_id', 'is', null);
    if (draftsErr) throw draftsErr;
    if (!drafts || drafts.length === 0) return { created: 0 };

    // 2. The check_draft_ids that already have a bank_transaction.
    const draftIds = drafts.map((d: Record<string, unknown>) => d['id'] as string);
    const { data: existing, error: existingErr } = await this.sb
      .from('bank_transactions')
      .select('check_draft_id')
      .in('check_draft_id', draftIds);
    if (existingErr) throw existingErr;
    const covered = new Set(
      (existing ?? []).map((r: Record<string, unknown>) => r['check_draft_id'] as string),
    );

    // 3. For each draft missing its bank_transaction, fall back to the
    //    most recent state-change event date for transaction_date, and
    //    rely on `_insertBankTransaction` for idempotent insert + balance recalc.
    const missing = drafts.filter((d: Record<string, unknown>) => !covered.has(d['id'] as string));
    if (missing.length === 0) return { created: 0 };

    let created = 0;
    for (const d of missing) {
      const id    = d['id'] as string;
      const state = d['current_state'] as CheckDraftState;
      const txDate = await this._lookupSettlementDate(id, state) ?? (d['issue_date'] as string) ?? new Date().toISOString().slice(0, 10);
      await this._insertBankTransaction({
        bankAccountId:   d['bank_account_id'] as string,
        direction:       state === 'cashed' ? 'in' : 'out',
        transactionType: d['type'] as string,
        amount:          d['amount'] as number,
        transactionDate: txDate,
        reference:       (d['number'] as string) ?? '',
        checkDraftId:    id,
        notes:           (d['notes'] as string) ?? null,
      });
      created += 1;
    }
    return { created };
  }

  /**
   * Best-effort lookup of when this draft entered its current settled state,
   * so the back-filled bank_transaction reflects the real settlement date
   * rather than today. Returns null if no event row is found.
   */
  private async _lookupSettlementDate(
    checkDraftId: string,
    toState: CheckDraftState,
  ): Promise<string | null> {
    const { data } = await this.sb
      .from('check_draft_events')
      .select('event_date')
      .eq('check_draft_id', checkDraftId)
      .eq('to_state', toState)
      .order('created_at', { ascending: false })
      .limit(1);
    const row = (data ?? [])[0] as { event_date?: string } | undefined;
    return row?.event_date ?? null;
  }

  // ---- private helpers ----

  private async _insertEvent(params: {
    checkDraftId: string;
    fromState: CheckDraftState | null;
    toState: CheckDraftState;
    eventDate: string;
    notes?: string;
  }): Promise<void> {
    const { error } = await this.sb.from('check_draft_events').insert({
      check_draft_id: params.checkDraftId,
      from_state:     params.fromState,
      to_state:       params.toState,
      event_date:     params.eventDate,
      notes:          params.notes ?? null,
    });
    // Non-fatal: log but don't throw so the main operation still succeeds
    if (error) {
      console.warn('[checks-repository] failed to insert event:', error.message);
    }
  }

  private async _insertBankTransaction(params: {
    bankAccountId: string;
    direction: 'in' | 'out';
    transactionType: string;
    amount: number;
    transactionDate: string;
    reference: string;
    checkDraftId: string;
    notes: string | null;
  }): Promise<void> {
    // bank_transactions table is created by Treasury migration 20260418120000.

    // Idempotency — if a bank_transaction already exists for this check_draft_id
    // we don't insert a duplicate (cashed/debited are terminal so this normally
    // can't re-fire, but a retry after a failed state update could double-insert).
    const { data: existing } = await this.sb
      .from('bank_transactions')
      .select('id')
      .eq('check_draft_id', params.checkDraftId)
      .limit(1);
    if (existing && existing.length > 0) return;

    // Column name is `description` on bank_transactions (not `notes`) — using
    // `notes` here makes PostgREST return 400 ("Could not find the 'notes'
    // column in the schema cache"), and the surrounding code swallows the
    // error so every transition silently produced no bank_transactions row.
    const { error } = await this.sb.from('bank_transactions').insert({
      bank_account_id:  params.bankAccountId,
      direction:        params.direction,
      transaction_type: params.transactionType,
      amount:           params.amount,
      transaction_date: params.transactionDate,
      reference:        params.reference,
      check_draft_id:   params.checkDraftId,
      description:      params.notes,
    });
    if (error) {
      console.warn('[checks-repository] failed to insert bank_transaction:', error.message);
      return;
    }

    // Refresh bank_accounts.current_balance via the recalculate RPC so the
    // treasury page reflects the new balance immediately.
    const { error: recalcErr } = await this.sb
      .rpc('recalculate_bank_account_balance', { p_account_id: params.bankAccountId });
    if (recalcErr) {
      console.warn('[checks-repository] balance recalc failed:', recalcErr.message);
    }
  }
}

// ---- mapper ----

function mapCheckDraft(row: Record<string, unknown>): CheckDraft {
  return {
    id:              row['id'] as string,
    type:            row['type'] as CheckDraft['type'],
    direction:       row['direction'] as CheckDraft['direction'],
    number:          (row['number'] as string) ?? '',
    bankName:        (row['bank_name'] as string) ?? '',
    bankAccountId:   (row['bank_account_id'] as string) ?? null,
    payerBankId:     (row['payer_bank_id'] as string) ?? null,
    payerRib:        (row['payer_rib'] as string) ?? null,
    beneficiaryName: (row['beneficiary_name'] as string) ?? null,
    placeOfPayment:  (row['place_of_payment'] as string) ?? null,
    placeOfIssue:    (row['place_of_issue'] as string) ?? null,
    protestable:     (row['protestable'] as boolean) ?? false,
    amount:          row['amount'] as number,
    issueDate:       (row['issue_date'] as string) ?? '',
    dueDate:         (row['due_date'] as string) ?? '',
    acceptanceDate:  (row['acceptance_date'] as string) ?? null,
    thirdPartyType:  row['third_party_type'] as CheckDraft['thirdPartyType'],
    thirdPartyId:    row['third_party_id'] as string,
    currentState:    row['current_state'] as CheckDraftState,
    isEndorsed:      (row['is_endorsed'] as boolean) ?? false,
    endorsedTo:      (row['endorsed_to'] as string) ?? null,
    paymentId:       (row['payment_id'] as string) ?? null,
    printedAt:       (row['printed_at'] as string) ?? null,
    notes:           (row['notes'] as string) ?? null,
    createdAt:       row['created_at'] as string,
  };
}

function mapEvent(row: Record<string, unknown>): CheckDraftEvent {
  return {
    id:            row['id'] as string,
    checkDraftId:  row['check_draft_id'] as string,
    fromState:     (row['from_state'] as CheckDraftState) ?? null,
    toState:       row['to_state'] as CheckDraftState,
    eventDate:     row['event_date'] as string,
    notes:         (row['notes'] as string) ?? null,
    createdBy:     (row['created_by'] as string) ?? null,
    createdAt:     row['created_at'] as string,
  };
}

function isMissingColumnError(error: unknown, column: string): boolean {
  if (!error || typeof error !== 'object') return false;
  const record = error as { code?: unknown; message?: unknown };
  return record.code === 'PGRST204'
    && typeof record.message === 'string'
    && record.message.includes(`'${column}' column`);
}
